import axios from 'axios';

const aiApi = {};

aiApi.generateSimpleExercise = async (subject, options = {}) => {
    try {
        const response = await axios.post('/api/private/ai/generate-exercise', {
            subject,
            ...options,
        });
        return response.data;
    } catch (error) {
        console.error('Error generating simple exercise:', error);
        throw error;
    }
};

aiApi.generateExerciseWithSolution = async (subject, options = {}) => {
    try {
        const response = await axios.post('/api/private/ai/generate-exercise-with-solution', {
            subject,
            ...options,
        });
        return response.data;
    } catch (error) {
        console.error('Error generating exercise with solution:', error);
        throw error;
    }
};

aiApi.explainConcept = async (subject, concept, options = {}) => {
    try {
        const response = await axios.post('/api/private/ai/explain-concept', {
            subject,
            concept,
            ...options,
        });
        return response.data;
    } catch (error) {
        console.error('Error explaining concept:', error);
        throw error;
    }
};

// One exercise per student. `type` is 'simple' | 'solution' | 'concept'; options carry
// count, gradeLevel, language, topic and (for concept) concept. Resolves to
// { variants: [{title, components, metadata}], failed }.
aiApi.generateExerciseSet = async (type, subject, options = {}) => {
    try {
        const response = await axios.post('/api/private/ai/generate-exercise-set', {
            type,
            subject,
            ...options,
        });
        return response.data;
    } catch (error) {
        console.error('Error generating exercise set:', error);
        throw error;
    }
};

// Board clean-up: `blob` is the board's ink composited on white as PNG. Goes as multipart so
// the 100 KB JSON body limit does not apply. Resolves { components, metadata }.
// The clean-up is a background job on the server (a high-thinking model can take minutes):
// POST starts it and answers with a job id, GET reports its state until it is done or failed.
aiApi.startTidyJob = async (blob, { language } = {}) => {
    const form = new FormData();
    form.append('file', blob, 'board.png');
    if (language) form.append('language', language);
    const response = await axios.post('/api/private/ai/tidy-board', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

aiApi.getTidyJob = async (jobId) => {
    const response = await axios.get(`/api/private/ai/tidy-board/${encodeURIComponent(jobId)}`);
    return response.data;
};

const TIDY_POLL_FIRST_MS = 1500;
const TIDY_POLL_EVERY_MS = 2000;
const TIDY_POLL_MAX_MS = 6 * 60 * 1000;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Starts the job and polls it to completion. Resolves with { components, metadata }; rejects
// on a failed job, a lost job, or when nothing came back within TIDY_POLL_MAX_MS. A transient
// network error on one poll is retried on the next tick rather than failing the clean-up.
aiApi.tidyBoard = async (blob, { language, onProgress } = {}) => {
    try {
        const { jobId } = await aiApi.startTidyJob(blob, { language });
        if (!jobId) throw new Error('No job id returned');
        const startedAt = Date.now();
        let wait = TIDY_POLL_FIRST_MS;
        while (Date.now() - startedAt < TIDY_POLL_MAX_MS) {
            await sleep(wait);
            wait = TIDY_POLL_EVERY_MS;
            let job;
            try {
                job = await aiApi.getTidyJob(jobId);
            } catch (error) {
                if (error.response && error.response.status === 404) throw new Error('Clean-up job lost');
                console.warn('Clean-up poll failed, retrying', error);
                continue;
            }
            if (onProgress) onProgress({ elapsedMs: Date.now() - startedAt, status: job.status });
            if (job.status === 'done') return { components: job.components || [], metadata: job.metadata };
            if (job.status === 'failed') throw new Error(job.error || 'Clean-up failed');
        }
        throw new Error('Clean-up timed out');
    } catch (error) {
        console.error('Error tidying the board:', error);
        throw error;
    }
};

// Only reached as a fallback when the image search finds nothing. Concurrent callers are
// deduplicated a layer up, in common/aiIllustration.js, which covers the search path too.
aiApi.generateIllustration = async (prompt, options = {}) => {
    try {
        const response = await axios.post('/api/private/ai/generate-image', {
            prompt,
            ...options,
        });
        return response.data;
    } catch (error) {
        console.error('Error generating illustration:', error);
        throw error;
    }
};

aiApi.detectFunction = async (latex) => {
    const response = await axios.post('/api/private/ai/detect-function', { latex });
    return response.data;
};

export default aiApi;
